import { isAxiosError } from 'axios';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type {
  CogsReportPreview,
  FulfillmentAdminClient,
  FulfillmentOrderLine,
} from '@/infrastructure/FulfillmentAdminClient';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import { LinePicker } from '@/pages/fulfillment/orders/components/LinePicker';

export const COGS_REPORT_TITLE = 'COGS Report';

/** The channel the catalog team reads (its real Slack name); the button and the success line name it the same way. */
export const COGS_REPORT_CHANNEL = '#catalogue-mgmt';

/** A purchase price is whole won; thousands separators are allowed because that is how a won price is copied. */
const PURCHASE_PRICE = /^\d{1,3}(,\d{3})*$|^\d+$/;

/** How long typing pauses before the preview is asked for again, so it is not rebuilt on every key. */
const PREVIEW_DELAY_MS = 250;

const numberOf = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

/** What one unit sold for, in the order's currency: the snapshot's sale price, the figure the line's Total is built from. */
export const unitSalePriceOf = (line: Pick<FulfillmentOrderLine, 'snapshot'>): number | null => {
  const snapshot = line.snapshot && typeof line.snapshot === 'object' ? (line.snapshot as Record<string, unknown>) : {};
  const salePrice = numberOf(snapshot['salePrice']);
  return salePrice !== null && salePrice >= 0 ? Math.round(salePrice * 100) / 100 : null;
};

/** Whole won as the box shows it: digits only, with thousands separators (10000 → 10,000); anything else is dropped. */
export const formatWon = (text: string): string => {
  const digits = text.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  return digits ? digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '';
};

/** The purchase price the form opens with: the line's Product Cost in whole won, empty when it has none. */
export const purchaseDraftOf = (line: Pick<FulfillmentOrderLine, 'productCost'>): string =>
  line.productCost == null ? '' : formatWon(String(Math.round(line.productCost)));

/** The won figure beside the sale price, or why there is none; `undefined` while the preview is on its way. */
export const wonText = (preview: CogsReportPreview | null | undefined): string => {
  if (preview === undefined) {
    return 'Reading the won rate…';
  }
  if (preview === null) {
    return 'Could not read the won rate';
  }
  if (preview.salePrice === null) {
    return 'The line has no sale price';
  }
  if (preview.salePriceKrw === null) {
    return 'No won rate on the server';
  }
  // The store's own rate, not a market one, so the figure says which it is.
  return `≈ ₩${preview.salePriceKrw.toLocaleString('en-US')} at the store rate`;
};

const unescapeSlack = (text: string): string => text.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');

/** `*bold*`, `<url|label>` and `<@id>` (the only markup the report uses), so a pasted `*` in a name stays text. */
const SLACK_MARKUP = /\*([^*\n]+)\*|<@([A-Z0-9]+)>|<([^<>|]+)\|([^<>]+)>/g;

/** One line of the message, as Slack would draw it: bold, links by their label, mentions by name. */
export const slackLineParts = (line: string, names: Record<string, string>): ReactNode[] => {
  const parts: ReactNode[] = [];
  let at = 0;
  for (const match of line.matchAll(SLACK_MARKUP)) {
    const start = match.index ?? 0;
    if (start > at) {
      parts.push(unescapeSlack(line.slice(at, start)));
    }
    const [, bold, mention, url, label] = match;
    if (bold !== undefined) {
      parts.push(<strong key={start}>{unescapeSlack(bold)}</strong>);
    } else if (mention !== undefined) {
      parts.push(
        <span key={start} className="cr-mention">
          @{names[mention] ?? mention}
        </span>,
      );
    } else {
      parts.push(
        <a key={start} href={url} target="_blank" rel="noreferrer">
          {unescapeSlack(label ?? '')}
        </a>,
      );
    }
    at = start + match[0].length;
  }
  if (at < line.length) {
    parts.push(unescapeSlack(line.slice(at)));
  }
  return parts;
};

/** A v4 UUID for the press; randomUUID needs a secure context, so the random bytes stand in where it is missing. */
export const newRequestId = (): string => {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Picks the request id when Send is pressed. A post the same as the last one tried keeps that try's id, so a double
 * press or a retry after a timeout is answered once by the server; a different post gets a new id, so an edit after a
 * failed send is posted and not answered with the first try's stored reply. Edits that are never sent change nothing.
 */
export const useRequestIdForSend = (): ((postKey: string) => string) => {
  const lastTry = useRef<{ key: string; id: string } | null>(null);
  return (postKey: string) => {
    if (lastTry.current === null || lastTry.current.key !== postKey) {
      lastTry.current = { key: postKey, id: newRequestId() };
    }
    return lastTry.current.id;
  };
};

/** What a failed Send tells the operator, and whether the post may still have gone out. */
export interface SendFailure {
  text: string;
  unsure: boolean;
}

/**
 * A 4xx is a refusal: nothing was posted. Any other failure (no answer, a timeout, a 5xx with or without a reason) may
 * have come after the post, so the operator is told to check the channel and what resending does.
 */
export const sendFailureOf = (err: unknown, what: string, reason: string | undefined): SendFailure => {
  const status = isAxiosError(err) ? err.response?.status : undefined;
  if (status !== undefined && status >= 400 && status < 500) {
    return { text: reason || `The ${what} could not be sent. Nothing was posted.`, unsure: false };
  }
  const note =
    `The ${what} may or may not have been posted. Check ${COGS_REPORT_CHANNEL} before changing it: sending the ` +
    'same again reuses its request id, so the server answers it once, but a changed one goes out as a separate message.';
  return { text: reason ? `${reason} ${note}` : `No clear answer from the server. ${note}`, unsure: true };
};

const failureOf = (err: unknown): SendFailure => {
  const message = isAxiosError<{ message?: string | string[] }>(err) ? err.response?.data?.message : undefined;
  return sendFailureOf(err, 'COGS report', Array.isArray(message) ? message.join(' · ') : message);
};

export interface CogsReportDialogProps {
  client: FulfillmentAdminClient;
  orderId: number;
  currency: string;
  lines: FulfillmentOrderLine[];
  onClose: () => void;
  onSent: (message: string) => void;
}

/**
 * Reports an overpriced product to the catalog team (SSD-7328): pick the line, check the purchase price (KRW, filled
 * from Product Cost) and send. The sale price is what the line sold for, so it is shown, not typed. Below the fields
 * the form shows the message exactly as Send will post it, built by the server from the same code as the post. The
 * dialog keeps one width and its line table fixed columns, whichever line is picked.
 */
export function CogsReportDialog({ client, orderId, currency, lines, onClose, onSent }: CogsReportDialogProps) {
  const dialogRef = useModalDialog(onClose);
  const [lineNo, setLineNo] = useState<number | null>(lines[0]?.lineNo ?? null);
  const picked = useMemo(() => lines.find((line) => line.lineNo === lineNo) ?? null, [lines, lineNo]);
  const [purchaseDraft, setPurchaseDraft] = useState(() => (lines[0] ? purchaseDraftOf(lines[0]) : ''));
  const [preview, setPreview] = useState<CogsReportPreview | null | undefined>(undefined);
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState('');
  // Kept through edits until the dialog closes: an edited Send after it may post a second message.
  const [unsure, setUnsure] = useState('');

  const purchaseText = purchaseDraft.trim();
  const purchaseValid = PURCHASE_PRICE.test(purchaseText);
  const purchasePrice = purchaseValid ? Number(purchaseText.replace(/,/g, '')) : null;
  const requestIdFor = useRequestIdForSend();

  useEffect(() => {
    if (picked === null) {
      return undefined;
    }
    let ignore = false;
    const timer = setTimeout(() => {
      client
        .previewLineCogsReport(orderId, picked.lineNo, purchasePrice)
        .then((view) => !ignore && setPreview(view))
        .catch(() => !ignore && setPreview(null));
    }, PREVIEW_DELAY_MS);
    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [client, orderId, picked, purchasePrice]);

  const pick = (line: FulfillmentOrderLine) => {
    if (line.lineNo !== lineNo) {
      setPreview(undefined);
    }
    setLineNo(line.lineNo);
    setPurchaseDraft(purchaseDraftOf(line));
    setRefusal('');
  };

  const salePrice = preview ? preview.salePrice : picked ? unitSalePriceOf(picked) : null;
  const blocked = sending || picked === null || purchasePrice === null;

  const submit = async () => {
    if (blocked || picked === null || purchasePrice === null) {
      return;
    }
    setSending(true);
    setRefusal('');
    try {
      const requestId = requestIdFor(JSON.stringify([orderId, picked.lineNo, purchasePrice]));
      await client.reportLineCogs(orderId, picked.lineNo, { purchasePrice, requestId });
      onSent(`COGS report for line ${picked.lineNo} (${picked.sku}) sent to ${COGS_REPORT_CHANNEL}.`);
      onClose();
    } catch (err) {
      const failure = failureOf(err);
      if (failure.unsure) {
        setUnsure(failure.text);
      } else {
        setRefusal(failure.text);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <dialog className="wms-prompt catalog-post cogs-report" ref={dialogRef} aria-label={COGS_REPORT_TITLE}>
      <header>
        {COGS_REPORT_TITLE}
        <button type="button" className="x" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body">
        <p className="cr-lead">
          Sends one message to {COGS_REPORT_CHANNEL} tagging Yun and Linh, with the order, the product and both prices.
        </p>
        <LinePicker lines={lines} selectedLineNo={lineNo} onSelect={pick} radioName="cogs-report-line" />
        <div className="cr-fields">
          <div className="cr-field">
            <span>Sale price per unit ({currency})</span>
            <output className="cr-fixed" aria-label={`Sale price per unit (${currency})`}>
              {salePrice === null ? '—' : salePrice.toFixed(2)}
            </output>
            <span className="cr-won" data-testid="cogs-sale-won">
              {wonText(preview)}
            </span>
          </div>
          <label className="cr-field">
            <span>Purchase price per unit (₩)</span>
            {/* The ₩ sits inside the box and the digits group as they are typed, as a won price is read. */}
            <span className="cr-money" aria-invalid={!purchaseValid}>
              <span className="cr-unit" aria-hidden="true">
                ₩
              </span>
              <input
                inputMode="numeric"
                aria-label="Purchase price per unit (₩)"
                aria-invalid={!purchaseValid}
                value={purchaseDraft}
                onChange={(e) => setPurchaseDraft(formatWon(e.target.value))}
              />
            </span>
            <span className="cr-won" />
          </label>
        </div>
        <div className="cr-hint">
          The sale price is what the line sold for, so it cannot be changed. The purchase price starts from Product
          Cost; change it if it is wrong.
        </div>
        <section className="cr-preview" aria-label="Message preview">
          <div className="cr-preview-title">What Send posts to {COGS_REPORT_CHANNEL}</div>
          <div className="cr-preview-box" data-testid="cogs-message-preview">
            {preview === undefined && <div className="cr-preview-note">Building the preview…</div>}
            {preview === null && <div className="cr-preview-note">Could not build the preview. Send still works.</div>}
            {preview &&
              preview.text.split('\n').map((line, index) => (
                <div key={index} className="cr-preview-line">
                  {slackLineParts(line, preview.mentionNames)}
                </div>
              ))}
          </div>
        </section>
        {refusal && (
          <div className="cr-refusal" role="alert">
            {refusal}
          </div>
        )}
        {unsure && (
          <div className="cr-refusal" role="alert">
            {unsure}
          </div>
        )}
      </div>
      <div className="foot">
        <button type="button" className="btn btn-line btn-sm" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-blue btn-sm" disabled={blocked} onClick={() => void submit()}>
          {sending ? 'Sending…' : `Send to ${COGS_REPORT_CHANNEL}`}
        </button>
      </div>
    </dialog>
  );
}
